import React, { useEffect, useState, useRef } from 'react';
import api from '../api';
import { 
    Maximize, ZoomIn, ZoomOut, Camera, 
    Map as MapIcon, Grip, X 
} from 'lucide-react';

const THEME = {
    card: '#1e293b', border: '#334155', textSec: '#94a3b8', 
    primary: '#3b82f6', success: '#10b981', danger: '#ef4444', warning: '#facc15'
};

const CELL_SIZE = 40; 

const getRowLabel = (index) => {
    let label = '';
    let i = index;
    while (i >= 0) {
        label = String.fromCharCode((i % 26) + 65) + label;
        i = Math.floor(i / 26) - 1;
    }
    return label;
};

const SiteMap = () => {
    const [mapItems, setMapItems] = useState([]);
    const [selectedCell, setSelectedCell] = useState(null);
    const [bgImage, setBgImage] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    
    // --- CONTEXT STATE ---
    const [activeSiteId, setActiveSiteId] = useState(null);
    const [siteName, setSiteName] = useState('Loading...');

    // --- GRID STATE ---
    const [numRows, setNumRows] = useState(10);
    const [numCols, setNumCols] = useState(10);
    const [isSaving, setIsSaving] = useState(false);
    const [canEdit, setCanEdit] = useState(false); 
    
    // --- TRANSFORM STATE ---
    const [transform, setTransform] = useState({ x: 0, y: 0, scale: 1 });
    const [isDragging, setIsDragging] = useState(false);
    
    // --- REFS ---
    const containerRef = useRef(null);
    const fileInputRef = useRef(null);
    const lastPos = useRef({ x: 0, y: 0 }); 
    const pinchDist = useRef(null); 

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        
        init();

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (numRows && numCols && containerRef.current) {
            setTimeout(fitToScreen, 100);
        }
    }, [numRows, numCols, bgImage, isMobile]);

    // --- INITIALIZATION (FIXED) ---
    const init = async () => {
        try {
            // 1. Get User Details
            const userRes = await api.get('users/me/');
            const user = userRes.data;
            
            // 2. Determine Permissions
            const isOwner = user.is_superuser || 
                            user.is_staff || 
                            (user.company_details?.type === 'OWNER');
            setCanEdit(isOwner);
            
            // 3. Determine Active Site (The Fix: Check ALL possible ID fields)
            let targetSiteId = user.current_site_id || user.current_site;
            
            // Handle edge case where it returns an object instead of an ID
            if (targetSiteId && typeof targetSiteId === 'object') {
                targetSiteId = targetSiteId.id;
            }

            if (targetSiteId) {
                setActiveSiteId(targetSiteId);
                
                // Try to find the name in the company sites list for display
                let foundName = `Site #${targetSiteId}`;
                if (user.company_details?.sites) {
                    const siteObj = user.company_details.sites.find(s => s.id === targetSiteId);
                    if (siteObj) foundName = `${siteObj.name} (${siteObj.code})`;
                }
                setSiteName(foundName);
                
                // Load the specific map data
                loadData(targetSiteId);
            } else {
                // Fallback: If no site selected in profile, try to fetch list and pick first
                try {
                    const sitesRes = await api.get('sites/');
                    if (sitesRes.data && sitesRes.data.length > 0) {
                        const firstSite = sitesRes.data[0];
                        setActiveSiteId(firstSite.id);
                        setSiteName(`${firstSite.name} (${firstSite.code})`);
                        loadData(firstSite.id);
                    } else {
                        setSiteName("No Sites Available");
                    }
                } catch (e) {
                    setSiteName("No Site Selected");
                }
            }
        } catch (err) { 
            console.error(err); 
            setSiteName("Authentication Error");
        }
    };

    const loadData = async (siteId) => {
        if (!siteId) return;
        try {
            // Get Items
            const resData = await api.get(`map-data/?site_id=${siteId}`);
            setMapItems(resData.data);
            
            // Get Config
            const resConfig = await api.get(`config/map-settings/?site_id=${siteId}`);
            
            if (resConfig.data) {
                if (resConfig.data.site_name) setSiteName(resConfig.data.site_name);
                
                setNumRows(parseInt(resConfig.data.rows) || 10);
                setNumCols(parseInt(resConfig.data.cols) || 10);
                if (resConfig.data.image) setBgImage(resConfig.data.image); 
            }
        } catch (err) { console.error("Error loading map data", err); }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file || !activeSiteId) return;
        
        const formData = new FormData();
        formData.append('image', file);
        formData.append('site_id', activeSiteId); 

        try {
            setIsSaving(true);
            const res = await api.patch('config/map-settings/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            setBgImage(res.data.image); 
            alert("✅ Map Uploaded");
        } catch (err) { alert("Error uploading map"); } 
        finally { setIsSaving(false); }
    };

    const handleGridSizeChange = async (type, val) => {
        const num = parseInt(val);
        if (num > 0 && num <= 300 && activeSiteId) { 
            if (type === 'rows') setNumRows(num);
            else setNumCols(num);
            try {
                const payload = type === 'rows' ? { rows: num, site_id: activeSiteId } : { cols: num, site_id: activeSiteId };
                await api.patch('config/map-settings/', payload); 
            } catch (err) { console.error("Failed to save grid size", err); }
        }
    };

    // --- TRANSFORM ENGINE (Unchanged) ---
    const fitToScreen = () => {
        if (!containerRef.current) return;
        const cw = containerRef.current.clientWidth;
        const ch = containerRef.current.clientHeight;
        const mw = numCols * CELL_SIZE;
        const mh = numRows * CELL_SIZE;
        let scale = Math.min(cw / mw, ch / mh) * 0.95; 
        if (!scale || scale < 0.01) scale = 0.5;
        const x = (cw - mw * scale) / 2;
        const y = (ch - mh * scale) / 2;
        setTransform({ x, y, scale });
    };

    const handleWheel = (e) => {
        if (!containerRef.current.contains(e.target)) return;
        e.preventDefault(); 
        const scaleSpeed = 0.001;
        const newScale = Math.max(0.1, Math.min(transform.scale - e.deltaY * scaleSpeed, 5));
        setTransform(prev => ({ ...prev, scale: newScale }));
    };

    const handleMouseDown = (e) => {
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'BUTTON') return;
        setIsDragging(true);
        lastPos.current = { x: e.clientX, y: e.clientY };
    };

    const handleMouseMove = (e) => {
        if (!isDragging) return;
        const dx = e.clientX - lastPos.current.x;
        const dy = e.clientY - lastPos.current.y;
        lastPos.current = { x: e.clientX, y: e.clientY };
        setTransform(prev => ({ ...prev, x: prev.x + dx, y: prev.y + dy }));
    };

    const handleMouseUp = () => setIsDragging(false);

    const handleTouchStart = (e) => {
        if (e.touches.length === 1) {
            lastPos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        } else if (e.touches.length === 2) {
            const d = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
            pinchDist.current = d;
        }
    };

    const handleTouchMove = (e) => {
        if (e.cancelable && e.target.tagName !== 'INPUT') e.preventDefault();
        if (e.touches.length === 1) {
            const dx = e.touches[0].clientX - lastPos.current.x;
            const dy = e.touches[0].clientY - lastPos.current.y;
            lastPos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
            setTransform(prev => ({ ...prev, x: prev.x + dx, y: prev.y + dy }));
        } else if (e.touches.length === 2 && pinchDist.current) {
            const d = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
            const ratio = d / pinchDist.current;
            setTransform(prev => ({ ...prev, scale: Math.max(0.2, Math.min(prev.scale * ratio, 5)) }));
            pinchDist.current = d;
        }
    };

    const handleCellClick = (e, gridRef, items) => {
        e.stopPropagation();
        setSelectedCell({ gridRef, items });
    };

    const getItemsForCell = (gridRef) => mapItems.filter(item => item.grid === gridRef);
    const rowsArray = Array.from({ length: numRows }, (_, i) => getRowLabel(i));
    const colsArray = Array.from({ length: numCols }, (_, i) => i + 1);

    const mapWidth = numCols * CELL_SIZE;
    const mapHeight = numRows * CELL_SIZE;

    return (
        <div className="animate-fade-in" style={{
            height: 'calc(100vh - 100px)', 
            display: 'flex', flexDirection: 'column', overflow: 'hidden' 
        }}>
            
            {/* TOOLBAR */}
            <div className="card" style={{
                padding: '10px 15px', background: THEME.card, borderBottom: `1px solid ${THEME.border}`,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 20, flexShrink: 0
            }}>
                <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
                    <MapIcon size={20} color={THEME.primary}/>
                    <div>
                        <h2 style={{marginTop:0, color: 'white', marginBottom: '0', fontSize: '1rem'}}>
                            {siteName}
                        </h2>
                    </div>
                </div>
                {canEdit && (
                    <div style={{display:'flex', gap:'8px', alignItems:'center'}}>
                        <div style={{display:'flex', alignItems:'center', background:'#0f172a', padding:'2px 8px', borderRadius:'6px', border:`1px solid ${THEME.border}`}}>
                            <Grip size={14} color={THEME.textSec} style={{marginRight:'5px'}}/>
                            <input type="number" value={numRows} onChange={e=>handleGridSizeChange('rows', e.target.value)} style={{width:'30px', background:'transparent', border:'none', color:'white', textAlign:'center', fontSize:'0.9rem'}} />
                            <span style={{color:THEME.textSec, margin:'0 4px'}}>×</span>
                            <input type="number" value={numCols} onChange={e=>handleGridSizeChange('cols', e.target.value)} style={{width:'30px', background:'transparent', border:'none', color:'white', textAlign:'center', fontSize:'0.9rem'}} />
                        </div>
                        
                        <button onClick={() => fileInputRef.current.click()} title="Upload Map Image" style={{padding:'6px', background:THEME.primary, border:'none', color:'white', borderRadius:'6px', cursor:'pointer', display:'flex', alignItems:'center'}}>
                            <Camera size={16}/>
                        </button>
                        <input type="file" accept="image/*" ref={fileInputRef} style={{display:'none'}} onChange={handleImageUpload} />
                    </div>
                )}
            </div>

            {/* --- MAP VIEWPORT --- */}
            <div 
                ref={containerRef}
                onWheel={handleWheel} onMouseDown={handleMouseDown} onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}
                onTouchStart={handleTouchStart} onTouchMove={handleTouchMove}
                style={{
                    flex: 1, background: '#0f172a', overflow: 'hidden', 
                    cursor: isDragging ? 'grabbing' : 'grab', touchAction: 'none', position: 'relative'
                }}
            >
                {/* TRANSFORM WRAPPER */}
                <div style={{
                    width: `${mapWidth}px`, height: `${mapHeight}px`,
                    transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
                    transformOrigin: '0 0', position: 'absolute', top: 0, left: 0,
                    willChange: 'transform', boxShadow: '0 0 50px rgba(0,0,0,0.5)', 
                    backgroundColor: '#1e293b'
                }}>
                    {bgImage ? (
                        <img src={bgImage} alt="Site Layout" draggable="false" style={{width: '100%', height: '100%', objectFit: 'fill', opacity: 0.8, pointerEvents: 'none', userSelect: 'none', display: 'block'}} />
                    ) : (
                        <div style={{width:'100%', height:'100%', border:`2px dashed ${THEME.border}`, display:'flex', alignItems:'center', justifyContent:'center', color:THEME.textSec}}>
                            <h3>{canEdit ? 'Upload Site Layout' : 'No Map Available'}</h3>
                        </div>
                    )}

                    {/* GRID */}
                    <div style={{
                        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                        display: 'grid', gridTemplateColumns: `repeat(${numCols}, ${CELL_SIZE}px)`,
                        gridTemplateRows: `repeat(${numRows}, ${CELL_SIZE}px)`
                    }}>
                        {rowsArray.map(row => (
                            <React.Fragment key={`row-${row}`}>
                                {colsArray.map(col => {
                                    const gridRef = `${row}${col}`;
                                    const items = getItemsForCell(gridRef);
                                    const hasCritical = items.some(i => i.status === 'CRITICAL');
                                    const hasHigh = items.some(i => i.status === 'HIGH');
                                    const hasWork = items.some(i => i.type === 'WORK_LOG');
                                    const isSelected = selectedCell?.gridRef === gridRef;

                                    let bg = 'transparent';
                                    let border = '1px solid rgba(255,255,255,0.05)';
                                    
                                    if (hasCritical) { bg = 'rgba(239, 68, 68, 0.6)'; border = '1px solid #ef4444'; }
                                    else if (hasHigh) { bg = 'rgba(249, 115, 22, 0.6)'; border = '1px solid #f97316'; }
                                    else if (hasWork) { bg = 'rgba(59, 130, 246, 0.6)'; border = '1px solid #3b82f6'; }

                                    if (isSelected) { bg = 'rgba(255, 255, 255, 0.4)'; border = '2px solid white'; }

                                    const showLabel = transform.scale > 0.6 || (col === 1 || row === 'A' || items.length > 0);

                                    return (
                                        <div key={gridRef} onClick={(e) => handleCellClick(e, gridRef, items)} style={{background: bg, border: border, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', color: 'rgba(255,255,255,0.5)', userSelect:'none'}}>
                                            {showLabel && (
                                                <span style={{fontWeight: items.length > 0 ? 'bold' : 'normal', color: items.length > 0 ? 'white' : 'rgba(255,255,255,0.3)'}}>
                                                    {items.length > 0 ? items.length : (col===1 ? row : col)}
                                                </span>
                                            )}
                                        </div>
                                    )
                                })}
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            </div>

            {/* CONTROLS */}
            <div style={{position: 'absolute', bottom: '20px', right: '20px', display: 'flex', flexDirection: 'column', gap: '10px', zIndex: 30}}>
                <button onClick={() => setTransform(p => ({...p, scale: Math.min(p.scale + 0.2, 5)}))} style={zoomBtnStyle}><ZoomIn size={20}/></button>
                <button onClick={fitToScreen} style={zoomBtnStyle}><Maximize size={20}/></button>
                <button onClick={() => setTransform(p => ({...p, scale: Math.max(p.scale - 0.2, 0.1)}))} style={zoomBtnStyle}><ZoomOut size={20}/></button>
            </div>

            {/* DETAILS PANEL */}
            {selectedCell && (
                <div style={{
                    position: 'fixed', top: isMobile ? 'auto' : '80px', right: isMobile ? '0' : '20px', bottom: isMobile ? '0' : '20px',
                    width: isMobile ? '100%' : '300px', maxHeight: isMobile ? '50vh' : 'calc(100vh - 120px)',
                    background: 'rgba(15, 23, 42, 0.95)', backdropFilter: 'blur(10px)',
                    border: `1px solid ${THEME.border}`, borderTop: `2px solid ${THEME.primary}`,
                    borderRadius: isMobile ? '16px 16px 0 0' : '12px', zIndex: 100, padding: '20px', display:'flex', flexDirection:'column',
                    animation: 'slideUp 0.2s ease', boxShadow: '0 -5px 25px rgba(0,0,0,0.5)'
                }}>
                    <div style={{display:'flex', justifyContent:'space-between', marginBottom:'10px'}}>
                        <h3 style={{margin:0, color:'white'}}>📍 Grid: {selectedCell.gridRef}</h3>
                        <button onClick={() => setSelectedCell(null)} style={{background:'transparent', border:'none', color:'white', cursor:'pointer'}}><X size={20}/></button>
                    </div>
                    <div style={{flex: 1, overflowY: 'auto'}}>
                        {selectedCell.items.length === 0 ? <p style={{color:THEME.textSec}}>Zone is clear.</p> : (
                            <div style={{display:'flex', flexDirection:'column', gap:'10px'}}>
                                {selectedCell.items.map((item, idx) => (
                                    <div key={idx} style={{padding:'10px', background: item.type === 'SAFETY' ? 'rgba(239,68,68,0.2)' : 'rgba(59,130,246,0.2)', borderRadius:'6px'}}>
                                        <div style={{color:'white', fontWeight:'bold'}}>{item.label}</div>
                                        <div style={{color:THEME.textSec, fontSize:'0.85rem'}}>{item.description}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
            <style>{`@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>
        </div>
    );
};

const zoomBtnStyle = { width: '45px', height: '45px', borderRadius: '50%', background: '#1e293b', border: '1px solid #475569', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 6px black' };

export default SiteMap;